/**
 * Time spent on a page, reported to Amplitude as "Page Left" when the user leaves it (route change,
 * tab closed, tab sent to the background). Only the time the tab is visible counts: a page left open
 * behind another window is not a visit. The value is capped so that one forgotten tab does not
 * dominate an average, and very short stays (redirects) are not reported at all.
 */

export const PAGE_LEFT_EVENT = "Page Left";

/** Beyond 30 minutes on a page, nobody is reading: the visit is capped there. */
export const PAGE_DURATION_CAP_MS = 30 * 60 * 1000;

/** Under a second, the page was a redirect, not a visit. */
export const PAGE_DURATION_MIN_MS = 1000;

export interface VisibleStopwatch {
  /** The tab went to the background: stop counting. */
  pause(at: number): void;
  /** The tab is visible again: count again. */
  resume(at: number): void;
  /** The page is left: the visible time in ms, capped. Further calls change nothing. */
  stop(at: number): number;
}

/**
 * A stopwatch that only counts while the tab is visible.
 * @param startedAt - Timestamp of the arrival on the page (ms)
 * @param visible - Whether the tab is visible at that moment
 */
export const createVisibleStopwatch = (startedAt: number, visible = true): VisibleStopwatch => {
  let elapsed = 0;
  let runningSince: number | null = visible ? startedAt : null;
  let stopped = false;

  const flush = (at: number) => {
    if (runningSince !== null) {
      elapsed += Math.max(0, at - runningSince);
      runningSince = null;
    }
  };

  return {
    pause: (at) => {
      if (!stopped) {
        flush(at);
      }
    },
    resume: (at) => {
      if (!stopped && runningSince === null) {
        runningSince = at;
      }
    },
    stop: (at) => {
      if (!stopped) {
        flush(at);
        stopped = true;
      }

      return Math.min(PAGE_DURATION_CAP_MS, Math.round(elapsed));
    },
  };
};

/** The event properties for a visit, or `null` when the stay was too short to be a visit. */
export const pageLeftProperties = (pathname: string, durationMs: number): { duration_ms: number; page_path: string } | null =>
  durationMs < PAGE_DURATION_MIN_MS ? null : { duration_ms: Math.round(durationMs), page_path: pathname };
